import { Stack, Typography } from "@mui/material";
import WhiteCard from "src/shared/components/WhiteCard";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useGetMessagesQuery } from "src/api/services/messagesApi";
import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback, useMemo } from "react";
import { useIsMobile } from "src/hooks/useIsMobile";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";
import { usePatientLocale } from "src/hooks/usePatientLocale";
import { enUS } from "date-fns/locale";
import { useMessageThreads } from "src/context/MessageThreadsProvider";
import { useNavigate } from "react-router-dom";

interface MessageCardProps {
  productSlug?: string;
  page: string;
}

const MessageCard = ({ productSlug, page }: MessageCardProps) => {
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const component = "messageCard";
  const locale = usePatientLocale();
  const { threads } = useMessageThreads();

  const thread = useMemo(() => {
    return (
      threads.find((thread) => thread.product.slug === productSlug) ?? null
    );
  }, [productSlug, threads]);

  const { data: messages = [] } = useGetMessagesQuery(thread?.id ?? skipToken);

  const lastMessage = useMemo(() => messages[messages.length - 1], [messages]);

  const handleNavigate = useCallback(() => {
    navigate(`/messaging/${thread?.id}`);
  }, [navigate, thread]);

  const formattedDate = useMemo(() => {
    if (lastMessage?.created_at == null) return null;

    const dateFormat = locale === enUS ? "MMM do" : "do 'de' MMM";

    return format(new Date(lastMessage.created_at), dateFormat, {
      locale,
    });
  }, [locale, lastMessage]);

  if (thread == null) {
    return null;
  }

  return (
    <WhiteCard
      sx={{ backgroundColor: "#D9F4F4", cursor: "pointer" }}
      onClick={handleNavigate}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack>
          <Typography fontWeight={600}>
            {t(`${page}.${component}.title`)}
          </Typography>
          <Typography>
            {t(`${page}.${component}.lastMessage`, { date: formattedDate })}
          </Typography>
        </Stack>
        {isMobile ? null : <NavigateNextIcon sx={{ color: "#00B6B9" }} />}
      </Stack>
    </WhiteCard>
  );
};

export default MessageCard;
